import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { supabase } from '../config/supabaseClient';
import { ENV } from '../config/env';

const BusinessPlanInvite: React.FC = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const redirectToLogin = useMemo(() => {
    const t = token ? String(token) : '';
    return `/login?redirect=${encodeURIComponent(`/business-plan/invite/${t}`)}`;
  }, [token]);

  useEffect(() => {
    const run = async () => {
      const t = token ? String(token) : '';
      if (!t) {
        setError('Missing invite token');
        setLoading(false);
        return;
      }

      try {
        const sessionResp = await supabase.auth.getSession();
        const accessToken = sessionResp?.data?.session?.access_token || '';

        if (!accessToken) {
          navigate(redirectToLogin);
          return;
        }

        const resp = await fetch(`${ENV.API_URL}/api/business-plans/collaboration/invites/accept/${encodeURIComponent(t)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(txt || 'Failed to accept invite');
        }

        const json = await resp.json();
        const businessPlanId = json?.data?.business_plan_id;
        if (!businessPlanId) {
          throw new Error('Missing business plan id');
        }

        toast.success('Invite accepted');
        navigate(`/business-plan/${businessPlanId}`);
      } catch (e: any) {
        console.error('Invite accept failed:', e);
        setError(e?.message || 'Failed to accept invite');
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, [token, navigate, redirectToLogin]);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-50">
      <div className="mx-auto w-full max-w-xl px-4 py-10">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-lg font-semibold text-gray-900">Business Plan Invite</div>
          <div className="mt-1 text-sm text-gray-600">
            {loading ? 'Accepting invite…' : null}
            {!loading && error ? error : null}
            {!loading && !error ? 'Redirecting…' : null}
          </div>

          {!loading && error ? (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Link
                to="/business-plan"
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
              >
                Open Business Plan Hub
              </Link>
              <Link
                to="/login"
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
              >
                Sign in
              </Link>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default BusinessPlanInvite;
